<mat-card class="default balance-collect-payment-card m-0 mb-4" data-balance-collect-payment-card>
  <mat-card-title>
    <span>{{ 'balance.collect-payment.title' | translate }}</span>
  </mat-card-title>

  <!-- Outstanding balance breakdown. Visible above the form so the operator
       sees the patient's residual liability before typing an amount. -->
  <div
    *ngIf="balance"
    class="balance-collect-payment-card__outstanding"
    data-balance-collect-payment-outstanding
  >
    <div class="balance-collect-payment-card__outstanding-row">
      <span>{{ 'balance.collect-payment.patient-responsibility' | translate }}</span>
      <span>{{ balance.patientResponsibility | currency:'USD' }}</span>
    </div>
    <div class="balance-collect-payment-card__outstanding-row">
      <span>{{ 'balance.collect-payment.paid' | translate }}</span>
      <span>−{{ balance.paidAmount | currency:'USD' }}</span>
    </div>
    <div *ngIf="balance.refundedAmount > 0" class="balance-collect-payment-card__outstanding-row">
      <span>{{ 'balance.collect-payment.refunded' | translate }}</span>
      <span>+{{ balance.refundedAmount | currency:'USD' }}</span>
    </div>
    <div class="balance-collect-payment-card__outstanding-row balance-collect-payment-card__outstanding-row--total">
      <strong>{{ 'balance.collect-payment.outstanding' | translate }}</strong>
      <strong data-balance-collect-payment-outstanding-amount>
        {{ outstanding | currency:'USD' }}
      </strong>
    </div>
    <div
      *ngIf="isBalanceOvercharged"
      class="balance-collect-payment-card__outstanding-row balance-collect-payment-card__outstanding-row--overcharge"
      data-balance-collect-payment-overcharge-row
    >
      <span>{{ 'balance.collect-payment.overcharge' | translate }}</span>
      <span data-balance-collect-payment-overcharge-amount>
        +{{ balanceOvercharge | currency:'USD' }}
      </span>
    </div>
  </div>

  <mat-tab-group
    class="balance-collect-payment-card__tabs"
    mat-stretch-tabs="false"
    mat-align-tabs="start"
    animationDuration="0ms"
    data-balance-collect-payment-tabs
  >
    <mat-tab [label]="'balance.collect-payment.tab.direct' | translate">
      <!-- Active payment plan banner: the backend rejects new direct charges while
       a Stripe Subscription Schedule is running. Hide the charge form entirely
       so the operator can't fill it out. -->
      <div
        *ngIf="hasActivePaymentPlan"
        class="balance-collect-payment-card__plan-active"
        data-balance-collect-payment-plan-active
      >
        <mat-icon color="primary">info</mat-icon>
        <span>{{ 'balance.collect-payment.plan-active-notice' | translate }}</span>
      </div>

      <form
        *ngIf="!hasActivePaymentPlan"
        [formGroup]="form"
        (ngSubmit)="submit()"
        class="balance-collect-payment-card__form"
        autocomplete="off"
      >
        <mat-card-content>
          <div class="balance-collect-payment-card__method-row">
            <mat-radio-group
              formControlName="paymentMethod"
              class="balance-collect-payment-card__methods"
              data-balance-collect-payment-method
            >
              <mat-radio-button *ngIf="showCreditCardOption" color="primary" value="creditCard">
                {{ 'balance.payment.method.credit-card' | translate }}
              </mat-radio-button>
              <mat-radio-button color="primary" value="check">
                {{ 'balance.payment.method.check' | translate }}
              </mat-radio-button>
              <mat-radio-button color="primary" value="cash">
                {{ 'balance.payment.method.cash' | translate }}
              </mat-radio-button>
            </mat-radio-group>

            <mat-checkbox
              *ngIf="showDiscount"
              color="primary"
              formControlName="applyDiscount"
              class="balance-collect-payment-card__discount"
              data-balance-collect-payment-discount
            >
              {{ 'balance.payment.apply-discount' | translate }} ({{ discountPercent }}%)
            </mat-checkbox>
          </div>

          <!--
            Kept in the DOM unconditionally (via `[hidden]`, not `*ngIf`) so the picker
            can fetch saved cards eagerly on patient bind. The cardsLoaded handler runs
            the legacy radio default + first-card pre-select side-effects regardless of
            whether the dropdown is currently visible.
          -->
          <mmx-saved-card-select
            class="balance-collect-payment-card__field"
            formControlName="paymentMethodId"
            [hidden]="!(form.value.paymentMethod === 'creditCard' && showCreditCardOption)"
            [patientId]="patient?.id ?? null"
            [allowNewCard]="allowNewCard"
            testId="balance-collect-payment-saved-card"
            (cardsLoaded)="onSavedCardsLoaded($event)"
          ></mmx-saved-card-select>

          <ng-container *ngIf="isAddingNewCard">
            <mat-form-field appearance="fill" class="balance-collect-payment-card__field">
              <mat-label>{{ 'balance.payment.holder-name' | translate }}</mat-label>
              <input matInput formControlName="holderName" data-balance-collect-payment-holder-name />
            </mat-form-field>
            <mat-form-field
              appearance="fill"
              class="balance-collect-payment-card__field"
            >
              <mat-label>{{ 'common.email' | translate }}</mat-label>
              <input matInput type="email" formControlName="email" data-balance-collect-payment-email />
              <mat-error *ngIf="form.controls.email.hasError('email')" data-balance-collect-payment-email-error>
                {{ 'balance.payment.invalid-email' | translate }}
              </mat-error>
            </mat-form-field>

            <mat-form-field
              *ngIf="isTilledProvider"
              appearance="fill"
              class="balance-collect-payment-card__field"
            >
              <mat-label>{{ 'balance.payment.billing-address' | translate }}</mat-label>
              <mmx-address-field
                formControlName="holderAddress"
                data-balance-collect-payment-billing-address
              ></mmx-address-field>
            </mat-form-field>

            <mmx-payment-card-form
              class="balance-collect-payment-card__stripe"
              [providerConfiguration]="providerConfiguration"
              [formConfiguration]="{ forcedSave: false, allowSave: true }"
              (formUpdated)="onCardFormUpdated()"
              data-balance-collect-payment-card-form
            ></mmx-payment-card-form>
          </ng-container>

          <mat-form-field
            *ngIf="form.value.paymentMethod === 'check'"
            appearance="fill"
            class="balance-collect-payment-card__field"
          >
            <mat-label>{{ 'balance.payment.check-number' | translate }}</mat-label>
            <input
              matInput
              formControlName="checkNumber"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              placeholder="1000"
              data-balance-collect-payment-check-number
            />
            <mat-hint>{{ 'balance.payment.check-number-hint' | translate }}</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="fill" class="balance-collect-payment-card__field">
            <mat-label>{{ 'balance.payment.amount' | translate }}</mat-label>
            <span matTextPrefix class="balance-collect-payment-card__amount-prefix">$&nbsp;</span>
            <input
              matInput
              type="number"
              step="0.01"
              min="0.01"
              formControlName="amount"
              data-balance-collect-payment-amount
            />
            <mat-hint *ngIf="isOvercharging" class="balance-collect-payment-card__overcharge-hint" data-balance-collect-payment-overcharge-hint>
              <ng-container *ngIf="hasPriorPayments">
                {{ 'balance.payment.overcharge-after-paid' | translate: { amount: (overchargeAmount | currency:'USD') } }}
              </ng-container>
              <ng-container *ngIf="!hasPriorPayments">
                {{ 'balance.payment.overcharge' | translate: { amount: (overchargeAmount | currency:'USD') } }}
              </ng-container>
            </mat-hint>
          </mat-form-field>

          <div class="balance-collect-payment-card__visibility">
            <mat-slide-toggle
              color="primary"
              formControlName="areNotesVisibleToPatient"
              [matTooltip]="'balance.payment.visible-to-patient-tooltip' | translate"
              matTooltipPosition="right"
              data-balance-collect-payment-visible-toggle
            >
              {{ 'balance.payment.visible-to-patient' | translate }}
            </mat-slide-toggle>
          </div>

          <mat-form-field appearance="fill" class="balance-collect-payment-card__field">
            <mat-label>{{ 'balance.payment.notes' | translate }}</mat-label>
            <textarea
              matInput
              formControlName="notes"
              rows="2"
              data-balance-collect-payment-notes
            ></textarea>
            <mat-hint>{{ 'balance.payment.notes-hint' | translate }}</mat-hint>
          </mat-form-field>

          <mmx-custom-fields-form
            *ngIf="transactionFields$ | async as fields"
            class="balance-collect-payment-card__custom-fields"
            [fields]="fields"
            formControlName="customFields"
            data-balance-collect-payment-custom-fields
          ></mmx-custom-fields-form>

          <mat-progress-bar
            *ngIf="submitting()"
            mode="indeterminate"
            class="balance-collect-payment-card__progress"
            data-balance-collect-payment-progress
          ></mat-progress-bar>

          <p
            *ngIf="error()"
            class="balance-collect-payment-card__error"
            data-balance-collect-payment-error
          >
            {{ error() }}
          </p>
        </mat-card-content>

        <mat-card-actions>
          <button
            mat-flat-button
            [color]="isOvercharging ? 'warn' : 'primary'"
            type="submit"
            class="balance-collect-payment-card__charge"
            [class.balance-collect-payment-card__charge--overcharge]="isOvercharging"
            [disabled]="!canSubmit()"
            data-balance-collect-payment-submit
            [attr.data-overcharging]="isOvercharging || null"
          >
            {{ 'balance.payment.charge' | translate }}
            <span class="balance-collect-payment-card__charge-amount">{{ chargeAmount | currency:'USD' }}</span>
            <span
              *ngIf="isOvercharging"
              class="balance-collect-payment-card__charge-overcharge"
              data-balance-collect-payment-charge-overcharge
            >
              {{ 'balance.payment.charge-overcharge-suffix' | translate: { amount: (overchargeAmount | currency:'USD') } }}
            </span>
          </button>
        </mat-card-actions>
      </form>
    </mat-tab>

    <mat-tab *ngIf="clinic?.modules?.billing" [label]="'balance.collect-payment.tab.plan' | translate">
      <mat-card-content class="balance-collect-payment-card__plan-tab">
        <mmx-balance-payment-plan-panel
          [balance]="balance"
          [clinic]="clinic"
          [patientId]="patient?.id ?? null"
          [patient]="patient"
          (planCreated)="onPaymentPlanCreated($event)"
          (planCanceled)="onPaymentPlanCanceled($event)"
        ></mmx-balance-payment-plan-panel>
      </mat-card-content>
    </mat-tab>
  </mat-tab-group>
</mat-card>
